// src/features/tasks/components/TaskDetailsPage/TaskHeader.jsx
import React from 'react';
import { Box, Button, Typography, Chip, LinearProgress } from '@mui/material';
import {
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  getStateConfig,
  isTaskOverdue,
} from '../../utils/taskStateUtils';
import { calculateDeadlineProgress, calculateDayMarkers } from '../../utils/deadlineProgressUtils';
import { useTranslation } from 'react-i18next';
import TaskActionButtons from '../TaskActionButtons';
import PageHeader from '../../../../components/common/PageHeader';

export default function TaskHeader({
  task,
  onBack,
  onEdit,
  onCopy,
  onStateChange,
  loading,
  title = null,
  showActions = true,
  details = null,
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const stateConfig = task ? getStateConfig(task.state) : null;
  const StateIcon = stateConfig?.icon;
  const isOverdue = task ? isTaskOverdue(task) : false;

  const { percent: progressPercent, barColor: progressBarColor } = task
    ? calculateDeadlineProgress({
        scheduledDate: task.scheduled_date,
        dueDate: task.due_date,
        completionDate: task.completion_date,
      })
    : { percent: 0, barColor: 'success.light' };

  const dayMarkers = task
    ? calculateDayMarkers({
        scheduledDate: task.scheduled_date,
        dueDate: task.due_date,
      })
    : [];

  return (
    <Box sx={{ mb: 4 }}>
      <PageHeader
        mb={4}
        onBack={onBack}
        title={task ? task.title : title || t('tasks:task_details')}
        subtitle={task ? t('tasks:details.task_details_and_management') : t('tasks:details.task_information')}
        actions={<>
          {showActions && onCopy && (
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={onCopy} disabled={loading}>
              {t('common:buttons.copy')}
            </Button>
          )}
          {showActions && onEdit && task?.state !== "COMPLETED" && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
              {t('common:buttons.edit')}
            </Button>
          )}
          {task?.state === "COMPLETED" && !onEdit && (
            <Typography variant="overline" color="text.secondary">
              {t("tasks:messages.no_change")}
            </Typography>
          )}
        </>}
      />

      {/* Status and Actions Section */}
      {task && showActions && (
        <Box sx={{ 
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          {/* Status and Actions Row */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 3,
            mb: 3
          }}>
            {/* Status Side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={StateIcon ? <StateIcon /> : undefined}
                label={stateConfig?.label || t('common:unknown')}
                color={stateConfig?.color || 'default'}
                size="medium"
                sx={{ 
                  fontSize: '0.875rem',
                  height: 40,
                  px: 1,
                  '& .MuiChip-icon': { fontSize: 20 }
                }}
              />
              
              {isOverdue && (
                <Chip
                  icon={<WarningIcon />}
                  label={t('tasks:details.overdue')}
                  color="error"
                  variant="filled"
                  size="medium"
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    height: 40,
                    '& .MuiChip-icon': { fontSize: 18 }
                  }}
                />
              )}

              {task.state !== "COMPLETED" && task.days_until_due !== undefined && !isOverdue && task.days_until_due <= 7 && (
                <Chip
                  label={
                    task.days_until_due === 0 ? t('tasks:details.due_today') : 
                    task.days_until_due === 1 ? t('tasks:details.due_tomorrow') :
                    t('tasks:details.days_left', { count: task.days_until_due })
                  }
                  color="warning"
                  variant="outlined"
                  size="medium"
                  sx={{ 
                    fontSize: '0.875rem',
                    height: 40,
                    fontWeight: 500
                  }}
                />
              )}
            </Box>

            {/* Actions Side */}
            <TaskActionButtons
              task={task}
              onStateChange={onStateChange}
              loading={loading}
              maxVisibleButtons={3}
            />
          </Box>

          {/* Progress Bar Section */}
          <Box>
            <Box sx={{ position: 'relative', height: 12 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  backgroundColor: 'background.default',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: progressBarColor,
                    borderRadius: 6,
                    transition: 'transform 0.4s ease-in-out'
                  }
                }}
              />

              {/* Day Markers */}
              {dayMarkers.map((marker) => (
                <Box
                  key={`day-marker-${marker.dayNumber}`}
                  sx={{
                    position: 'absolute',
                    left: `${marker.percent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#00000020',
                  }}
                />
              ))}
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1
            }}>
              <Typography variant="body2" color="text.primary">
                {`${t('tasks:details.scheduled')}: ${new Date(task.scheduled_date).toLocaleDateString()}`}
              </Typography>

              <Typography variant="body2" color="text.primary">
                {task.completion_date
                  ? `${t('tasks:data_grid.deadline_cell.completed')}: ${new Date(task.completion_date).toLocaleDateString()}`
                  : `${t('tasks:data_grid.deadline_cell.due')}: ${new Date(task.due_date).toLocaleDateString()}`}
              </Typography>
            </Box>
          </Box>

          {/* Key facts (priority, category, people) */}
          {details && (
            <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' }}>
              {details}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}